import clsx from "clsx";
import { CheckCheck, Paperclip, Plus, Receipt, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useQuickAdd } from "@/components/AppShell";
import { Button } from "@/components/ui/Button";
import { PendingTag } from "@/components/PendingTag";
import { DateRange } from "@/components/ui/DateRange";
import { Select } from "@/components/ui/Field";
import { Badge, EmptyState, ErrorState, Spinner } from "@/components/ui/Feedback";
import { Chip, PageHeader } from "@/components/ui/Layout";
import { Modal } from "@/components/ui/Modal";
import { Money } from "@/components/ui/Money";
import { SortableTh, Table, Td, Th, THead, Tr, type SortDir } from "@/components/ui/Table";
import { formatDate, TRANSACTION_TYPE_LABEL } from "@/lib/format";
import { useAccounts, useCategories, useTransactions } from "@/lib/queries";
import { TransactionDetailView } from "./TransactionDetail";
import { displayAmount, secondaryText, transactionTitle, TransactionRow, TypeIcon } from "./TransactionRow";

const FILTER_KEYS = ["q", "type", "account_id", "category_id", "date_from", "date_to", "status", "sort",
                     "import_batch_id"] as const;
const TYPES = ["expense", "income", "transfer", "refund", "adjustment"] as const;

export function TransactionsPage() {
  const { today } = useAuth();
  const quickAdd = useQuickAdd();
  const [params, setParams] = useSearchParams();
  const accounts = useAccounts();
  const categories = useCategories();
  const [search, setSearch] = useState(params.get("q") ?? "");

  // Filters live in the query string: shareable, bookmarkable "saved views".
  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [params]);

  function update(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if ((params.get("q") ?? "") !== search.trim()) update({ q: search.trim() || null });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = useTransactions(filters);
  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];
  const sort = filters.sort ?? "-date";
  const sortDir = (field: string): SortDir =>
    sort === field ? "asc" : sort === `-${field}` ? "desc" : null;
  const toggleSort = (field: string) =>
    update({ sort: sort === `-${field}` ? field : `-${field}` === "-date" ? null : `-${field}` });

  const accountName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? "Account";
  const categoryName = (id: string) => categories.data?.find((c) => c.id === id)?.name ?? "Category";
  const openId = params.get("txn");
  const perspective = filters.account_id;
  const active = Object.keys(filters).filter((k) => k !== "sort").length;

  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle="Every change is recorded. Voided transactions are kept, not deleted."
        actions={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => quickAdd()}>Add</Button>}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative min-w-52 flex-1 sm:max-w-xs">
          <span className="sr-only">Search transactions</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description, merchant, amount…"
            className="h-9 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm placeholder:text-ink-3 focus:border-accent focus:outline-none"
          />
        </label>
        <DateRange
          today={today}
          allowEmpty
          value={filters.date_from && filters.date_to ? { from: filters.date_from, to: filters.date_to } : null}
          onChange={(r) => update({ date_from: r?.from ?? null, date_to: r?.to ?? null })}
        />
        <Select aria-label="Account" value={filters.account_id ?? ""} className="!h-9 !w-auto text-sm"
                onChange={(e) => update({ account_id: e.target.value || null })}>
          <option value="">All accounts</option>
          {accounts.data?.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </Select>
        <Select aria-label="Category" value={filters.category_id ?? ""} className="!h-9 !w-auto text-sm"
                onChange={(e) => update({ category_id: e.target.value || null })}>
          <option value="">All categories</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>{c.parent_id ? `— ${c.name}` : c.name} ({c.category_type})</option>
          ))}
        </Select>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2" aria-label="Type filters">
        {TYPES.map((t) => {
          const selected = (filters.type ?? "").split(",").filter(Boolean);
          const on = selected.includes(t);
          return (
            <Chip key={t} active={on} onClick={() => {
              const next = on ? selected.filter((x) => x !== t) : [...selected, t];
              update({ type: next.join(",") || null });
            }}>
              {TRANSACTION_TYPE_LABEL[t]}
            </Chip>
          );
        })}
        <Select aria-label="Status" value={filters.status ?? "active"} className="!h-8 !w-auto text-sm"
                onChange={(e) => update({ status: e.target.value === "active" ? null : e.target.value })}>
          <option value="active">Active</option>
          <option value="voided">Voided</option>
          <option value="all">Active + voided</option>
        </Select>
        {filters.account_id && <Chip active onRemove={() => update({ account_id: null })}>{accountName(filters.account_id)}</Chip>}
        {filters.category_id && <Chip active onRemove={() => update({ category_id: null })}>{categoryName(filters.category_id)}</Chip>}
        {filters.import_batch_id && <Chip active onRemove={() => update({ import_batch_id: null })}>From import</Chip>}
        {active > 0 && (
          <button type="button" className="text-sm font-medium text-accent hover:underline"
                  onClick={() => { setSearch(""); setParams({}, { replace: true }); }}>
            Clear filters
          </button>
        )}
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Receipt className="size-8" />}
          title={active ? "No transactions match these filters" : "No transactions yet"}
          body={active ? "Try widening the date range or clearing filters." : "Record your first income or expense — it takes a few seconds."}
          action={!active ? <Button variant="primary" onClick={() => quickAdd()}>Add a transaction</Button> : undefined}
        />
      ) : (
        <div className={clsx(query.isFetching && !query.isFetchingNextPage && "opacity-70")}>
          {/* Phones: compact rows */}
          <ul className="rounded-xl border border-border bg-surface md:hidden">
            {rows.map((t) => <TransactionRow key={t.id} txn={t} perspective={perspective} />)}
          </ul>
          {/* Desktop: sortable table with sticky header */}
          <Table className="hidden max-h-[70vh] md:block" caption="Transactions">
            <THead>
              <tr>
                <SortableTh label="Date" dir={sortDir("date")} onSort={() => toggleSort("date")} />
                <Th>Description</Th>
                <Th>Category</Th>
                <Th>Account</Th>
                <SortableTh label="Amount" align="right" dir={sortDir("amount")} onSort={() => toggleSort("amount")} />
              </tr>
            </THead>
            <tbody>
              {rows.map((t) => (
                <Tr key={t.id} className={clsx("cursor-pointer hover:bg-surface-2", t.is_voided && "opacity-60")}
                    onClick={() => update({ txn: t.id })}>
                  <Td className="whitespace-nowrap text-ink-2">{formatDate(t.transaction_date)}</Td>
                  <Td>
                    <button type="button" className="flex items-center gap-2 text-left" onClick={(e) => { e.stopPropagation(); update({ txn: t.id }); }}>
                      <span className="text-ink-3"><TypeIcon t={t} /></span>
                      <span className={clsx("font-medium text-ink", t.is_voided && "line-through")}>{transactionTitle(t)}</span>
                      {t.is_voided && <Badge>Voided</Badge>}
                      {t.is_pending && <PendingTag />}
                      {t.is_reconciled && <CheckCheck className="size-3.5 text-ink-3" aria-label="Reconciled" />}
                      {t.attachment_count > 0 && <Paperclip className="size-3.5 text-ink-3" aria-label="Has attachment" />}
                    </button>
                    {secondaryText(t) && <span className="block text-xs text-ink-3">{secondaryText(t)}</span>}
                  </Td>
                  <Td className="text-ink-2">{t.transaction_type === "transfer" ? "Transfer" : t.transaction_type === "adjustment" ? "Adjustment" : t.category?.name}</Td>
                  <Td className="text-ink-2">{t.transaction_type === "transfer" ? `${t.account.name} → ${t.counter_account?.name}` : t.account.name}</Td>
                  <Td align="right"><Money value={displayAmount(t, perspective).value} currency={t.currency} signed={displayAmount(t, perspective).signed} tone={displayAmount(t, perspective).signed} className="font-medium" /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <div className="mt-3 flex justify-center">
            {query.hasNextPage ? (
              <Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button>
            ) : (
              <p className="text-xs text-ink-3">{rows.length} transaction{rows.length === 1 ? "" : "s"}</p>
            )}
          </div>
        </div>
      )}

      <Modal open={Boolean(openId)} onClose={() => update({ txn: null })} title="Transaction" size="md">
        {openId && <TransactionDetailView id={openId} onClose={() => update({ txn: null })} />}
      </Modal>
    </>
  );
}
